import storeManager from '../store/StoreManager';
import settingsReader from '../store/readHelpers/SettingsReader';
import sessionReader from '../store/readHelpers/SessionReader';
import apiService from './api/ApiService';
import apiReader from '../store/readHelpers/ApiReader';
import utilService from './UtilService';
import assessmentService from './AssessmentService';
import assessmentActions from '../actions/AssessmentActions';
import sessionActions from '../actions/SessionActions';
import catActions from '../actions/CatActions';

let requestCatQsInterval;
const REQUEST_CAT_QS_INTERVAL_TIME = 1000;

async function requestCatQuestionSet(assessmentRunId, itemRevisionIds) {
    try {
        const response = await apiService.requestAdaptiveQuestionSet(assessmentRunId, itemRevisionIds);
        return await handleRequestNewCatQuestionsDone(response);
    } catch (error) {
        handleRequestCatQuestionsFailure();
        throw error;
    }
}

async function handleRequestNewCatQuestionsDone(response) {
    if (!response.success) {
        return Promise.reject({
            errorType: 'Request new CAT qs failed',
            response,
            error: 'Failed to request a new CAT qs',
        });
    }
    return Promise.resolve();
}

function handleRequestCatQuestionsFailure() {
    console.log('Request new CAT questions failed');
}

async function postCatQuestionSetIdCurrentlyUsed(assessmentRunId, catRevisionId) {
    try {
        const response = await apiService.postCatQuestionSetId(assessmentRunId, catRevisionId);
        return await handlePostCatQuestionSetIdDone(response);
    } catch (error) {
        handlePostCatQuestionSetIdFailure();
        throw error;
    }
}

async function handlePostCatQuestionSetIdDone(response) {
    if (!response.success) {
        return Promise.reject({
            errorType: 'Post CAT question set id failed',
            response,
            error: 'Failed to post CAT currently used question set id',
        });
    }
    return Promise.resolve();
}

function handlePostCatQuestionSetIdFailure() {
    console.log('Post currently CAT question set id failed');
}

const catDeliveryService = {

    async handleUpdatedCatQS(catRevisionId) {
        const store = storeManager.getStore();
        store.dispatch(
            catActions.setCurrentCatRevisionId(catRevisionId)
        );

        const catAssessment = await apiService.fetchCatQuestionSet(catRevisionId);

        const { assessment, currentlyShown } = storeManager.getState();
        console.info(`CAT question set req. Current questionId: ${currentlyShown.questionId}, sectionId: ${currentlyShown.sectionId}`, catRevisionId);
        const assessmentCopy = utilService.deepCloneObject(assessment);
        const mergedAssessment = assessmentService.replaceQuestionsInAssessment(assessmentCopy, catAssessment);
        if (mergedAssessment) {
            console.info('Successfully merged CAT test', catRevisionId);
            store.dispatch(assessmentActions.assessmentLoadSuccess(mergedAssessment));
            postCatQuestionSetIdCurrentlyUsed(assessment.assessmentRunId, catRevisionId);
        } else {
            console.error('CAT questions cannot be merged, no changes to assessment made.', catRevisionId);
        }
        if (settingsReader.isCatSyncMode()) {
            store.dispatch(
                sessionActions.setCatTriggerInProgress(false)
            );
        }
    },

    getAnsweredQuestionsAndBufferQuestions(currentQuestionId, currentSectionId) {
        const state = storeManager.getState();
        const { sections = [] } = state.assessment;
        if (!currentQuestionId || !currentSectionId) {
            return [];
        }
        const catAdditionalResources = settingsReader.getCatTestAdditionalResources();
        const buffer = JSON.parse(catAdditionalResources).buffer;
        const currentSection = sections.find(section => section.id === currentSectionId);
        if (!currentSection) {
            return [];
        }
        const currentQuestion = currentSection.questions.find(question => question.contentRevisionId === currentQuestionId);
        if (!currentQuestion) {
            return [];
        }
        const currentQuestionIndex = currentSection.questions.findIndex(question => question.contentRevisionId === currentQuestionId);
        const answeredQuestions = currentSection.questions.filter(
            question => question.questionNumberInQuestionSet <= currentQuestion.questionNumberInQuestionSet);
        const questions = [
            ...answeredQuestions,
            ...currentSection.questions.slice(currentQuestionIndex + 1, currentQuestionIndex + 1 + buffer),
        ];
        return questions.map(question => question.contentRevisionId);
    },

    getAnsweredSectionsAndBufferSections(currentSectionId) {
        const state = storeManager.getState();
        const { sections = [] } = state.assessment;
        if (!currentSectionId) {
            return [];
        }
        const catAdditionalResources = settingsReader.getCatTestAdditionalResources();
        const buffer = JSON.parse(catAdditionalResources).buffer;
        const currentSectionIndex = sections.findIndex(section => section.id === currentSectionId);
        if (currentSectionIndex < 0) {
            return [];
        }
        const slicedSections = sections.slice(0, currentSectionIndex + 1 + buffer);
        return slicedSections.map(section => section.id);
    },

    async makeRequest(currentQuestionId, currentSectionId) {
        const state = storeManager.getState();
        const assessmentRunId = state.assessment.assessmentRunId;
        let itemRevisionIds;

        if (sessionReader.isQuestionItemBankLevel()) {
            itemRevisionIds = catDeliveryService.getAnsweredQuestionsAndBufferQuestions(currentQuestionId, currentSectionId);
        } else if (sessionReader.isSectionItemBankLevel()) {
            itemRevisionIds = catDeliveryService.getAnsweredSectionsAndBufferSections(currentSectionId);
        }
        return requestCatQuestionSet(assessmentRunId, itemRevisionIds);
    },

    /**
     * @param currentQuestionId The questionId shown at the time this function was called.
     * @param currentSectionId The sectionId shown at the time this function was called.
     */
    requestAdaptiveQuestionSetWhenReady(currentQuestionId, currentSectionId) {
        if (!requestCatQsInterval) {
            requestCatQsInterval = setInterval(
                () => {
                    if (!apiReader.hasUnsavedResponsesUpUntilQuestion(currentQuestionId)) {
                        clearInterval(requestCatQsInterval);
                        requestCatQsInterval = null;
                        // At this point state.currentlyShown.questionId will differ from currentQuestionId because of navigation to next.
                        this.makeRequest(currentQuestionId, currentSectionId);
                    }
                }, REQUEST_CAT_QS_INTERVAL_TIME);
        }
    },

    async requestAdaptiveQuestionSet(currentQuestionId, currentSectionId) {
        return this.makeRequest(currentQuestionId, currentSectionId);
    },
};

export default catDeliveryService;
